import * as React from 'react';
import { Tabs } from 'antd';
import { ButtonProps } from 'antd/lib';
import { interpolateString } from '@utils/string';
import {
  DELETE_SELECTED_PIPELINES_PLURAL_LABEL,
  DELETE_SELECTED_PIPELINES_SINGULAR_LABEL,
} from '@constants/data-page';
import { StatusCodes } from '@constants/enum/common';
import {
  PAGE_SIZE_PIPELINES_LIST,
  PIPELINES_LIST_DEFAULT_SORTING_KEY_ALL_TAB,
  PIPELINES_LIST_SORTING_DATATABLE_OPTIONS,
  TABS_OPTIONS_PIPELINE_LIST,
  TABS_PIPELINES_LIST_KEYS,
} from '@constants/pipelines';
import {
  IPipeline,
  PermissionAction,
  PermissionAsset,
  PipelineServiceLevel,
} from '@redux/types/types';
import Authorized from '@components/common/Authorized/Authorized';
import DataTable from '@components/dataTable/DataTable';
import RenderCreatePipelineDropdown from '@components/RenderCreatePipelineDropdown/RenderCreatePipelineDropdown';
import UpgradeBanner from '@modules/Upgrade/components/upgradeBanner/UpgradeBanner';
import { LimitCategory } from '@modules/Upgrade/consts/pricing';
import styles from './RenderPipelinesListTable.module.scss';

interface Props {
  activeTabPipelinesLandingPage: string | PipelineServiceLevel;
  onChangeTab: (key: string) => Promise<void>;
  isLimitedUser: boolean;
  totalPipelines: number;
  handleMenuCreatePipelineClick: () => void;
  fetchPipelinesListStatus: StatusCodes;
  loading: boolean;
  getPipelinesList: (
    currentPage: number,
    pageSize: number,
    searchValue: string,
    sortValue?: string,
    serviceLevel?: PipelineServiceLevel,
  ) => Promise<void>;
  pipelinesList: any;
  columns: any[];
  startPollingPipelinesList: (currentPage: number, pageSize: number, searchValue: string) => void;
  getEmptyLabelForTable: () => string;
  actionStatus: StatusCodes;
  stopPollingPipelinesList: () => void;
  sortValuePipelinesList: string;
  resetCurrentPage: boolean;
  totalSelectedPipelines: number;
  onDeleteMultiplePipelines: (pipelinesList: IPipeline[], pipelineNames: string[]) => Promise<void>;
  setTotalSelectedPipelines: (value: React.SetStateAction<number>) => void;
  newPipelineName: string;
  resetNewPipelineName: () => void;
  activatePipelineActionStatus: StatusCodes;
}

const RenderPipelinesListTable: React.FC<Props> = ({
  activeTabPipelinesLandingPage,
  onChangeTab,
  isLimitedUser,
  totalPipelines,
  handleMenuCreatePipelineClick,
  fetchPipelinesListStatus,
  loading,
  getPipelinesList,
  pipelinesList,
  columns,
  startPollingPipelinesList,
  getEmptyLabelForTable,
  actionStatus,
  stopPollingPipelinesList,
  sortValuePipelinesList,
  resetCurrentPage,
  totalSelectedPipelines,
  onDeleteMultiplePipelines,
  setTotalSelectedPipelines,
  newPipelineName,
  resetNewPipelineName,
  activatePipelineActionStatus,
}) => {
  const pipelineWritePermissions = { [PermissionAsset.PIPELINES]: PermissionAction.WRITE };

  return (
    <>
      <UpgradeBanner category={LimitCategory.CREDITS} />
      <Tabs
        className={styles.tabs}
        activeKey={activeTabPipelinesLandingPage || TABS_PIPELINES_LIST_KEYS.ALL}
        onChange={(activeKey) => onChangeTab(activeKey)}
        tabBarExtraContent={
          <Authorized requiredPermissions={pipelineWritePermissions} renderAsDisabled>
            <RenderCreatePipelineDropdown
              totalPipelines={totalPipelines}
              isLimitedUser={isLimitedUser}
              handleMenuCreatePipelineClick={handleMenuCreatePipelineClick}
            />
          </Authorized>
        }
        items={TABS_OPTIONS_PIPELINE_LIST.map((tab) => {
          return {
            label: tab.label,
            key: tab.key,
            disabled: fetchPipelinesListStatus === StatusCodes.IN_PROGRESS,
          };
        })}
      />

      <DataTable
        data={pipelinesList}
        total={totalPipelines}
        loading={loading}
        columns={columns}
        getData={(currentPage, pageSize, sortValue, searchValue) => {
          getPipelinesList(currentPage, pageSize, sortValue, searchValue);
        }}
        refetch={
          actionStatus === StatusCodes.SUCCESS ||
          activatePipelineActionStatus === StatusCodes.SUCCESS
        }
        rowKey="name"
        locale={{ emptyText: getEmptyLabelForTable() }}
        polling={{
          enabled: true,
          startPolling: startPollingPipelinesList,
          stopPolling: stopPollingPipelinesList,
        }}
        sorting={{
          selectedValue:
            sortValuePipelinesList === PIPELINES_LIST_DEFAULT_SORTING_KEY_ALL_TAB
              ? ''
              : sortValuePipelinesList,
          options: PIPELINES_LIST_SORTING_DATATABLE_OPTIONS,
          allowClear: activeTabPipelinesLandingPage?.length === 0,
        }}
        pagination={{
          pageSize: PAGE_SIZE_PIPELINES_LIST,
          currentPageNumber: resetCurrentPage ? 1 : undefined,
        }}
        selectActions={[
          {
            type: 'default' as ButtonProps['type'],
            danger: true,
            label: (totalSelectedPipelines === 1
              ? interpolateString(DELETE_SELECTED_PIPELINES_SINGULAR_LABEL, {
                  total: totalSelectedPipelines,
                })
              : interpolateString(DELETE_SELECTED_PIPELINES_PLURAL_LABEL, {
                  total: totalSelectedPipelines,
                })) as string,
            onClick: (pipelineNames) => onDeleteMultiplePipelines(pipelinesList, pipelineNames),
            permissions: pipelineWritePermissions,
          },
        ]}
        setTotalSelectedItems={(totalFilesSelected) =>
          setTotalSelectedPipelines(totalFilesSelected)
        }
        rowAnimation={{
          newRowName: newPipelineName,
          afterNewRowAnimation: () => resetNewPipelineName(),
        }}
      />
    </>
  );
};

export default RenderPipelinesListTable;
